import type { OtpChoiceChannel } from '@/modules/profile/api/profileApi'

export interface TelegramRecovery {
  botUrl: string | null
  channels: OtpChoiceChannel[]
  captchaSession: string
}

const record = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : null

const errorData = (error: unknown): Record<string, unknown> | null => {
  const source = record(error)
  return record(record(source?.response)?.data) ?? source
}

export const isTelegramDeliveryFailure = (error: unknown): boolean => {
  const data = errorData(error)
  return data?.result === 'error' && data.error_type === 'telegram_delivery_failed'
}

/** Metadata is untrusted: never infer email eligibility from the entered address. */
export const parseTelegramRecovery = (error: unknown): TelegramRecovery | null => {
  if (!isTelegramDeliveryFailure(error)) return null
  const data = record(errorData(error)?.telegram_recovery)
  if (!data || !Array.isArray(data.channels) || !data.channels.includes('telegram')
    || data.channels.some(channel => channel !== 'telegram' && channel !== 'email')
    || new Set(data.channels).size !== data.channels.length
    || typeof data.captcha_session !== 'string' || !data.captcha_session.trim()
    || (data.bot_url !== null && typeof data.bot_url !== 'string')) return null

  // Only the stateless bot-start marker is allowed, never login/link tokens or extra parameters.
  const botUrl = typeof data.bot_url === 'string'
    && /^https:\/\/t\.me\/[A-Za-z0-9_]{5,32}\?start=otp_retry$/.test(data.bot_url)
    ? data.bot_url : null
  return { botUrl, channels: [...data.channels], captchaSession: data.captcha_session }
}
